import { Route, Routes, useLocation, Navigate } from "react-router";
import "./../../App.css";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { lazy, Suspense, useEffect } from "react";
import { Box, Paper } from "@mui/material";
import { RESET_STATISTICS } from "../../redux/reducers/statisticsReducer";
import { ToastContainer } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import { isPrivileged } from "../../util/PrivilegeUtil";
import { useTranslation } from "react-i18next";

import { SearchCard } from "../card/SearchCard";

const AdministrateCards = lazy(() => import("../card/AdministrateCards"));
const AdministrateCareprincipal = lazy(
  () => import("../administration/carePrincipal/AdministrateCareprincipal")
);
const Group = lazy(() => import("../administration/group/Group"));
const UserDetailedInfo = lazy(() => import("../help/UserDetailedInfo"));
const AddCardTypes = lazy(() => import("../administration/AddCardTypes"));
const HSAImport = lazy(() => import("../administration/HSAImport"));
const Info = lazy(() => import("../administration/info/Info"));
const AddCarePrincipal = lazy(
  () => import("../administration/AddCarePrincipal")
);
const AdminSystemStatus = lazy(
  () => import("../administration/systemStatus/AdminSystemStatus")
);
const Statistics = lazy(() => import("../statistics/Statistics"));
const GeneralConfig = lazy(() => import("../administration/GeneralConfig"));
const CareUnitSettings = lazy(
  () => import("../administration/careUnit/CareUnitSettings")
);
const Authorization = lazy(() => import("../administration/Authorization"));
const KeyGroup = lazy(() => import("../administration/KeyGroup"));
const SearchOwnLogs = lazy(() => import("../logs/SearchOwnLogs"));
const SearchLogs = lazy(() => import("../logs/SearchLogs"));
const TrustManager = lazy(() => import("../administration/TrustManager"));
// COMFACT - KOMMER ATT TAS BORT 
// const ContractSuccess = lazy(() => import("../contract/ContractSuccess"));
const Contract = lazy(() => import("../contract/Contract"));
const FileImport = lazy(() => import("../administration/FileImport"));
// COMFACT - KOMMER ATT TAS BORT 
// const SignForm = lazy(() => import("../contract/SignForm"));

const RouteWrapper = ({ component: Component }: { component: React.ComponentType }) => (
  <Suspense fallback={<CircularProgress disableShrink />}>
    <Component />
  </Suspense>
);

const AdminRoute = ({ component: Component }: { component: React.ComponentType }) => {
  const session = useSelector((store: any) => store.session);
  const { t } = useTranslation();
  if (isPrivileged(session?.currentUser?.privileges, "ADMIN", "READ")) {
    return <RouteWrapper component={Component} />;
  }
  return (
    <div style={{ textAlign: "center" }}>
      <h1>{t("forbidden.status")}</h1>
      <h2>{t("forbidden.title")}</h2>
      <h3>{t("forbidden.message")}</h3>
    </div>
  );
};

export function WorkSpace() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isLoading = useSelector((store: any) => store.loadingState.loading);
  const isSearchCardPage = location.pathname.includes("search-card");
  const isSearchLogsPage = location.pathname.includes("search-logs");
  const statisticsHistory = location.pathname.includes("statistics");
  const isGroupPage = location.pathname.includes("group");
  const isAdminCardPage = location.pathname.includes("administrate-card");

  useEffect(() => {
    if (!statisticsHistory) {
      dispatch({ type: RESET_STATISTICS });
    }
    //Kör use effect om statistcsHistory ändras
  }, [statisticsHistory]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSearchCardPage === false) {
      dispatch({ type: "RESET_CARDS" });
      dispatch({ type: "RESET_TRANSACTION" });
    }
  }, [isSearchCardPage]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdminCardPage === false) {
      dispatch({ type: "RESET_CARDS" });
    }
  }, [isAdminCardPage]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isGroupPage === false) {
      dispatch({ type: "RESET_CARDS" });
    }
  }, [isGroupPage]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSearchLogsPage === false) {
      dispatch({ type: "RESET_LOGS" });
    }
  }, [isSearchLogsPage]); //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Paper className="work-space">
      <Box sx={{ width: "100%", textAlign: "center" }}>
        {isLoading ? (
          <CircularProgress
            disableShrink
            size={"30px"}
            sx={{ position: "fixed" }}
          />
        ) : null}
      </Box>

      {/* Tagit bort progressbar, kan vara förvirrande */}
      <ToastContainer
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Routes>
        <Route path="/" element={<Navigate to="/search-card" replace />} />
        <Route path="/search-card/*" element={<SearchCard />} />
        <Route path="/efrikort/*" element={<SearchCard />} />
        <Route path="/administration/authorization" element={<AdminRoute component={Authorization} />} />
        <Route path="/administration/fileimport" element={<AdminRoute component={FileImport} />} />
        <Route path="/administration/trustmanager" element={<AdminRoute component={TrustManager} />} />
        <Route path="/administration/keygroup" element={<AdminRoute component={KeyGroup} />} />
        <Route path="/administration/hsa-import" element={<AdminRoute component={HSAImport} />} />
        <Route path="/administration/info" element={<AdminRoute component={Info} />} />
        <Route path="/search-logs" element={<RouteWrapper component={SearchLogs} />} />
        <Route path="/search-own-logs" element={<RouteWrapper component={SearchOwnLogs} />} />
        <Route path="/statistics" element={<RouteWrapper component={Statistics} />} />
        <Route path="/careprincipal" element={<RouteWrapper component={AdministrateCareprincipal} />} />
        <Route path="/administration/group" element={<RouteWrapper component={Group} />} />
        <Route path="/administration/add-careprincipal" element={<AdminRoute component={AddCarePrincipal} />} />
        <Route path="/administration/add-cardtypes" element={<AdminRoute component={AddCardTypes} />} />
        <Route path="/administration/system-status" element={<AdminRoute component={AdminSystemStatus} />} />
        <Route path="/administration/general-config" element={<AdminRoute component={GeneralConfig} />} />
        <Route path="/administrate-card" element={<RouteWrapper component={AdministrateCards} />} />
        <Route path="/contract" element={<RouteWrapper component={Contract} />} />
        <Route path="/unit-settings" element={<RouteWrapper component={CareUnitSettings} />} />
        <Route path="/user" element={<RouteWrapper component={UserDetailedInfo} />} />
      </Routes>
    </Paper>
  );
}
